import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSigninMutation } from 'features/auth/hooks/use-auth';
import { useToast } from 'hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { useConfig } from 'hooks/use-config';
import { completeSigninWithOrganization } from 'features/auth/services/auth-service';
import { consumeOidcLoginState, getOidcRuntimeEnv } from 'lib/runtime-env';
import ErrorAlert from 'components/blocks/error-alert/error-alert';

export function RedirectPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { mutateAsync } = useSigninMutation<'authorization_code'>();
  const { login, setSelectedOrgId } = useAuthStore();
  const [searchParams] = useSearchParams();
  const { configurationSettings } = useConfig();
  const [error, setError] = useState<{ title: string; message: string } | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const callbackState = searchParams.get('state');
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (errorParam) {
      setError({
        title: errorParam.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
        message: errorDescription || 'An error occurred during authentication.',
      });
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 5000);
      return;
    }

    if (!code) {
      return;
    }

    (async () => {
      try {
        const storedState = consumeOidcLoginState();
        if (callbackState && storedState?.state && callbackState !== storedState.state) {
          navigate('/login', { replace: true });
          return;
        }

        const res = await mutateAsync({ grantType: 'authorization_code', code });
        const finalizedAuth = await completeSigninWithOrganization(res, configurationSettings);
        login(finalizedAuth.accessToken, finalizedAuth.refreshToken);
        setSelectedOrgId(finalizedAuth.organizationId);
        navigate(storedState?.goto || getOidcRuntimeEnv(configurationSettings.paths.baseUrl).postLoginPath, {
          replace: true,
        });
        toast({
          variant: 'success',
          title: t('SUCCESS'),
          description: t('LOGIN_SUCCESSFULLY'),
        });
      } catch (_error: any) {
        const errObj = _error?.error || _error;
        if (errObj?.error && errObj?.error_description) {
          setError({
            title: errObj.error.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
            message: errObj.error_description,
          });
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 5000);
        } else {
          navigate('/login', { replace: true });
        }
      }
    })();
  }, [configurationSettings, login, mutateAsync, navigate, searchParams, setSelectedOrgId, t, toast]);

  return (
    <div className="inset-0 z-50 flex flex-col items-center justify-center bg-background/80 backdrop-blur-sm">
      {error && <ErrorAlert isError={true} title={error.title} message={error.message} />}
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <h2 className="text-2xl font-bold text-high-emphasis">Redirecting...</h2>
    </div>
  );
}
